import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import { styled, css } from 'styled-components';
import BarsLoader, { Bar } from '../../Elements/BarsLoader';
import { Media } from '../../theme/Media';
const ButtonLoader = styled(BarsLoader) `
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  margin: auto;
  width: ${({ $square }) => ($square ? '24px' : '32px')};
  height: 18px;
`;
export const buttonStyles = css `
  position: relative;
  font-size: 12px;
  letter-spacing: 0.1px;
  line-height: 14px;
  font-weight: normal;
  color: ${({ theme }) => theme.colors.grey00_2}!important;
  padding: ${({ $styled: { square } }) => !square && '0 35px'};
  width: ${({ $styled: { square, icon, detailAction } }) => (square || icon || detailAction ? '36px' : '100%')};
  height: 36px;
  border-radius: 4px;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  border: none;
  outline: none;
  cursor: pointer;
  transition:
    box-shadow 0.3s,
    background-color 0.3s,
    color 0.3s;

  &:not([disabled]) {
    ${({ $styled: { variant }, theme }) => variant === 'create' &&
    css `
        background-color: ${theme.colors.main};

        &:hover {
          background-color: ${theme.colors.mainDark};
        }
      `}
    ${({ $styled: { variant }, theme }) => variant === 'accept' &&
    css `
        background-color: ${theme.colors.positive100};

        &:hover {
          background-color: ${theme.colors.positive120_1};
        }
      `}
    ${({ $styled: { variant }, theme }) => variant === 'edit' &&
    css `
        background-color: ${theme.colors.link100};

        &:hover {
          background-color: ${theme.colors.link120_1};
        }
      `}
    ${({ $styled: { variant }, theme }) => variant === 'delete' &&
    css `
        background-color: ${theme.colors.critical100};

        &:hover {
          background-color: ${theme.colors.critical120_1};
        }
      `}
    ${({ $styled: { variant, square }, theme }) => variant === 'create-secondary' &&
    !square &&
    css `
        background-color: transparent;
        border: 2px solid ${theme.colors.main};
        color: ${theme.colors.black_1}!important;

        &:hover {
          background-color: ${theme.colors.main};
          border-color: ${theme.colors.main};
          color: ${theme.colors.grey00_2}!important;
        }
        ${Bar} {
          background-color: ${theme.colors.black_1};
        }
      `}
    ${({ $styled: { variant, square }, theme }) => variant === 'create-secondary' &&
    square &&
    css `
        background-color: ${theme.colors.link100};

        &:hover {
          background-color: ${theme.colors.link120_1};
        }
      `}
  }

  &[disabled] {
    background-color: ${({ theme }) => theme.colors.grey70_1};
    cursor: not-allowed;

    &:hover {
      background-color: ${({ theme }) => theme.colors.grey70_1};
    }
    ${({ $styled: { variant, square }, theme }) => variant === 'create-secondary' &&
    !square &&
    css `
        border: 2px solid ${theme.colors.grey70_1};
      `}
  }
  ${({ $styled: { icon, pseudoDisabled, variant, square }, theme }) => !icon &&
    css `
      ${pseudoDisabled &&
        css `
        background-color: ${theme.colors.grey70_1}!important;
        cursor: not-allowed;
        pointer-events: none;
        box-shadow: none !important;
        border: ${variant === 'create-secondary' && !square && `2px solid ${theme.colors.grey70_1}`};
      `}
    `};
  ${({ $styled: { icon, detailAction, isLoading, pseudoDisabled }, theme }) => (icon || detailAction) &&
    css `
      min-width: 36px;
      padding: 0;
      font-size: ${!detailAction && '18px'};
      background: transparent;
      transition: opacity 0.3s;

      &,
      &:visited,
      &:hover {
        color: ${theme.colors.black_1}!important;

        &[disabled] {
          color: ${theme.colors.black_1}!important;
          background-color: transparent;
          box-shadow: none;
          opacity: ${() => !isLoading && 0.3};
        }
        ${pseudoDisabled &&
        css `
          color: ${theme.colors.black_1}!important;
          background-color: transparent;
          box-shadow: none;
          opacity: ${() => !isLoading && 0.3};
          pointer-events: none;
        `}
      }
      ${Bar} {
        background-color: ${theme.colors.black_1};
      }

      @media ${Media.mdMax} {
        width: 100%;
      }
    `};
`;
const ButtonContainer = styled.button `
  ${buttonStyles}
`;
export const Container = styled.div `
  position: relative;
  display: ${({ $styled: { square, inline } }) => (square || inline ? 'inline-flex' : 'flex')};
  cursor: ${({ $styled: { pseudoDisabled } }) => pseudoDisabled && 'not-allowed'};
`;
const ButtonContent = styled.div `
  opacity: ${({ $styled: { isLoading } }) => isLoading && 0};
  display: flex;
  align-items: center;
  white-space: nowrap;
`;
const Button = React.forwardRef(function Button({ isLoading = false, className = '', onClick = undefined, inline = false, children, square, icon, detailAction, variant = !icon && !detailAction && 'create', dataTestId, pseudoDisabled, ...other }, ref) {
    return (_jsx(Container, { "$styled": {
            square,
            inline,
            pseudoDisabled,
        }, children: _jsxs(ButtonContainer, { onClick: (e) => !isLoading && onClick && onClick(e), className: className, type: 'button', ref: ref, "$styled": {
                variant,
                square,
                icon,
                detailAction,
                isLoading,
                pseudoDisabled,
            }, "data-testid": dataTestId, ...other, children: [_jsx(ButtonContent, { "$styled": { isLoading }, children: children }), isLoading && _jsx(ButtonLoader, { "$square": square })] }) }));
});
Button.Container = Container;
export default Button;
